import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI, unwrapVendors, normalizeVendor } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES } from '../lib/period';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Building2, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ENTRY_TYPE_BADGE_CLASS = {
  BILL: 'border-rose-500/30',
  DEBIT_NOTE: 'border-orange-500/30 text-orange-400',
  CREDIT_NOTE: 'border-teal-500/30 text-teal-400',
};

const VendorLedgerPage = () => {
  const { selectedOrg } = useApp();
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);

  const loadVendors = useCallback(async () => {
    try {
      const response = await accountsAPI.getVendors(selectedOrg.id);
      const list = unwrapVendors(response.data);
      setVendors(list);
      if (list.length > 0 && !selectedVendor) {
        setSelectedVendor(list[0].vendor_id || list[0].id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendors([]);
    }
  }, [selectedOrg, selectedVendor]);

  const loadLedger = useCallback(async () => {
    if (!selectedVendor) return;
    setLoading(true);
    try {
      const params = {
        org_id: selectedOrg.id,
        vendor_id: selectedVendor,
      };
      Object.assign(params, periodState.apiParams);
      const response = await accountsAPI.getVendorLedger(params);
      setResolvedPeriod(response.data);
      // Backend returns `{vendor, entries, ...}`; the embedded `vendor`
      // is a raw EC object, so normalize it so the ledger header can
      // render `gstin` / `category` via legacy aliases.
      const data = response.data || {};
      setLedger(data.vendor ? { ...data, vendor: normalizeVendor(data.vendor) } : data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
    setLoading(false);
  }, [selectedOrg, selectedVendor, periodState.apiParams]);

  useEffect(() => {
    if (selectedOrg) {
      loadVendors();
    }
  }, [selectedOrg, loadVendors]);

  useEffect(() => {
    if (selectedVendor) {
      loadLedger();
    }
  }, [selectedVendor, periodState.apiParams, loadLedger]);

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-ledger-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Vendor Ledger</h1>
          <p className="text-muted-foreground mt-1">
            Track vendor transactions and balances
          </p>
        </div>
      </div>

      {/* Vendor Selector & Date Filter */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <Select value={selectedVendor} onValueChange={setSelectedVendor}>
              <SelectTrigger className="w-[300px] bg-background border-border/50" data-testid="vendor-selector">
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map(v => (
                  <SelectItem key={v.vendor_id || v.id} value={v.vendor_id || v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
            {ledger?.vendor && (
              <div className="flex gap-2 ml-auto">
                {ledger.vendor.gstin && (
                  <Badge variant="outline" className="font-mono text-xs">
                    GSTIN: {ledger.vendor.gstin}
                  </Badge>
                )}
                {ledger.vendor.category && (
                  <Badge variant="outline">{ledger.vendor.category}</Badge>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Closing Balance */}
      {ledger && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Debit (Bills)</p>
              <p className="text-2xl font-mono font-semibold text-rose-400 mt-1">
                {formatCurrency(ledger.entries?.reduce((sum, e) => sum + (e.debit || 0), 0))}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Total Credit (Payments)</p>
              <p className="text-2xl font-mono font-semibold text-emerald-400 mt-1">
                {formatCurrency(ledger.entries?.reduce((sum, e) => sum + (e.credit || 0), 0))}
              </p>
            </CardContent>
          </Card>
          <Card className={`bg-card/50 backdrop-blur-sm ${ledger.closing_balance > 0 ? 'border-amber-500/30' : 'border-emerald-500/30'}`}>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">Closing Balance</p>
              <p className={`text-2xl font-mono font-semibold mt-1 ${ledger.closing_balance > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {formatCurrency(Math.abs(ledger.closing_balance))}
                <span className="text-sm ml-2">{ledger.closing_balance > 0 ? '(Payable)' : '(Advance)'}</span>
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ledger Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardHeader>
          <CardTitle className="text-lg font-manrope">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={`skeleton-${i}`} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="font-manrope">Date</TableHead>
                  <TableHead className="font-manrope">Type</TableHead>
                  <TableHead className="font-manrope">Reference</TableHead>
                  <TableHead className="font-manrope">Description</TableHead>
                  <TableHead className="font-manrope text-right">Debit</TableHead>
                  <TableHead className="font-manrope text-right">Credit</TableHead>
                  <TableHead className="font-manrope text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger?.entries?.map((entry, idx) => (
                  <TableRow key={entry.reference_id || `${entry.date}-${entry.reference}`} className="border-border/50 hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">{formatDate(entry.date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {entry.type === 'BILL' || entry.type === 'DEBIT_NOTE' ? (
                          <ArrowUpRight className="w-4 h-4 text-rose-400" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-emerald-400" />
                        )}
                        <Badge variant="outline" className={ENTRY_TYPE_BADGE_CLASS[entry.type] || 'border-emerald-500/30'}>
                          {entry.type}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{entry.reference}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {entry.description}
                    </TableCell>
                    <TableCell className="text-right font-mono text-rose-400">
                      {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-emerald-400">
                      {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">
                      {formatCurrency(entry.balance)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!ledger?.entries || ledger.entries.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No transactions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorLedgerPage;
